/**
 * Services (V2) — ONE component per kind, a PIXEL-PERFECT literal clone of the reference
 * "feature" section (realDemo landing-feature.tsx). Reads `section.layout` and applies the
 * matching root class (services.css); the React tree mirrors the reference DOM exactly
 * (centered Info above a card list) and only CSS changes between layouts. The reference has
 * ONE feature design, so the frozen layouts cards | compact | icons all render identically.
 *
 * The reference feature row sits in a tonal band (`.landing-row.features { background:#f8f8f8 }`),
 * so it is rendered with Section tone="alt" — the foundation supplies that band and the
 * 100/80/40px vertical rhythm (no vertical padding lives in services.css).
 *
 * Public API unchanged (SectionComponentProps<'services'>) — Registry/Renderer/Engine
 * untouched. Reads only the shared content grammar: heading, subheading, items[], actions.
 * Each item maps to one reference feature card. Assets via the Asset Resolver.
 *
 * The reference card (`LandingRowFeatureListItem`) always renders its `<p>{item.content}</p>`
 * regardless of emptiness — reproduced literally below (no conditional) rather than omitting
 * the element when a feature has no description.
 *
 * The icon/image is wrapped in a fixed-size `.sbp-services__icon` box (services.css) so every
 * asset — whatever its intrinsic dimensions — renders at the exact same visual size, centered,
 * without stretching (object-fit: contain). A local wrapper here rather than a change to the
 * shared `Image`/Media components, which other sections (e.g. Reviews) also use unchanged.
 */
import type { SectionComponentProps } from '../../runtime/types'
import { Section, Container, InfoBlock, Image, cx } from '../shared'
import './services.css'

export function Services(p: SectionComponentProps<'services'>) {
  const { content, layout, id, theme, locale, assets } = p

  const titleId = `${id}-title`
  const items = content.items ?? []

  return (
    <Section
      as="section"
      tone="alt"
      theme={theme}
      locale={locale}
      id={id}
      ariaLabelledBy={content.heading ? titleId : undefined}
      className={cx('sbp-services', `sbp-services--${layout}`)}
    >
      <Container className="sbp-services__inner">
        <div className="sbp-services__info">
          <InfoBlock
            heading={content.heading}
            subheading={content.subheading}
            headingLevel={2}
            headingId={titleId}
            align="center"
            actions={content.actions}
          />
        </div>

        {items.length > 0 ? (
          <ul className="sbp-services__list">
            {items.map((item) => (
              <li key={item.id}>
                <div>
                  <span className="sbp-services__icon">
                    <Image asset={item.media ? assets.resolve(item.media.assetId) : null} alt="feature" />
                  </span>
                  <h4>{item.title}</h4>
                  <p>{item.description ?? ''}</p>
                </div>
              </li>
            ))}
          </ul>
        ) : null}
      </Container>
    </Section>
  )
}
